import React, { useState, useEffect, useRef } from "react";
import ReportsHelpers from "../../utils/ReportsHelpers";
import { syncInsightsTimeframeToUrl } from "../../utils/insightsTimeframe";
import ReportCount from "../reports/ReportCount";
import ReportChart from "../reports/ReportChart";
import ReportConversionRateChart from "../reports/ReportConversionRateChart";
import ReportFunnelChart from "../reports/ReportFunnelChart";
import updateConversionRate from "../../utils/updateConversionRate";
import updateAOV from "../../utils/updateAOV";
import Tooltip from "../shared/Tooltip";
import { reportingGoals } from "../../src/reportingGoals";
import ExportTriggerButton from "../shared/ExportTriggerButton";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fab } from "@fortawesome/free-brands-svg-icons";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { far } from "@fortawesome/free-regular-svg-icons";
library.add(fab, fas, far);
import { ColumnChart } from "react-chartkick";
import "chartkick/chart.js";
import readReportCount from "../../utils/readReportCount";
import {
  utcDayStart,
  utcDayEnd,
  utcDayLabel,
  pickerDate,
} from "../../utils/utcDayBoundary";

const WebsiteConversionReport = (props) => {
  const website = props.website;
  const goals = reportingGoals(props.reportingGoals || website);
  const reportsPath =
    props.reportsPath || `/websites/${website.id}/events_reports`;
  const reportWebsiteId = props.websiteIds || website.id;
  const checkoutsReportWebsiteId =
    (props.websiteIds && goals.checkoutsWebsiteIds) || reportWebsiteId;
  const bookingsReportWebsiteId =
    (props.websiteIds && goals.bookingsWebsiteIds) || reportWebsiteId;
  const customGoalReportWebsiteId =
    (props.websiteIds && goals.customGoalWebsiteIds) || reportWebsiteId;
  const scopeLabel = props.scopeLabel || "Site-wide";
  const defaultRangeOption = props.timeframeTypes.filter(
    (t) => t.value == "Last 24 Hours"
  )[0];
  const initialTimeframeLabel = props.initialTimeframeLabel || "Last 30 Days";
  const initialStartDate = props.initialStartDate
    ? new Date(`${props.initialStartDate}T00:00:00.000Z`).toISOString()
    : defaultRangeOption.range_start;
  const initialEndDate = props.initialEndDate
    ? new Date(`${props.initialEndDate}T23:59:59.999Z`).toISOString()
    : defaultRangeOption.range_end;
  const [metric, setMetric] = useState("conversions");
  const [analysisType, setAnalysisType] = useState("funnel");
  const [timeframeType, setTimeframeType] = useState(initialTimeframeLabel);
  const [rangeStartValue, setRangeStartValue] = useState(
    initialTimeframeLabel === "Date Range"
      ? initialStartDate
      : defaultRangeOption.range_start
  );
  const [rangeEndValue, setRangeEndValue] = useState(
    initialTimeframeLabel === "Date Range"
      ? initialEndDate
      : defaultRangeOption.range_end
  );
  const rangeStartInput = useRef(null);
  const rangeEndInput = useRef(null);
  const [revenueType, setRevenueType] = useState("attributed");
  const [subscribeSource, setSubscribeSource] = useState("convertflow");
  const contactSubscribeReports = Array.isArray(props.contact_subscribe_reports)
    ? props.contact_subscribe_reports
    : [];
  const selectedSubscribeReport = contactSubscribeReports.find(
    (report) => report.type === subscribeSource
  );
  const selectedSubscribeCount = selectedSubscribeReport
    ? selectedSubscribeReport.last_30_days || 0
    : 0;
  const selectedSubscribeCapped = selectedSubscribeReport?.capped === true;
  const selectedSubscribeChartLabel = selectedSubscribeCapped
    ? `${
        selectedSubscribeReport.type
      } (${selectedSubscribeCount.toLocaleString()}+)`
    : selectedSubscribeReport?.type;
  const last30DaysTimeframe = props.timeframeTypes.find(
    (t) => t.value == "Last 30 Days"
  );
  const showingIntegrationSubscribe =
    metric == "subscribes" &&
    subscribeSource !== "convertflow" &&
    !!selectedSubscribeReport &&
    !!last30DaysTimeframe;
  const [convertFlowCompareCount, setConvertFlowCompareCount] = useState(null);
  const [currentAOV, setCurrentAOV] = useState(0);
  const [comparisonAOV, setComparisonAOV] = useState(0);
  const [aovPercentageChange, setAovPercentageChange] = useState(null);
  const [aovLiftIsHovered, setAovLiftIsHovered] = useState(false);

  let group_by = ReportsHelpers.getGroupBy(timeframeType);
  let range_start = props.timeframeTypes.filter(
    (t) => t.value == timeframeType
  )[0].range_start;
  let range_end = props.timeframeTypes.filter(
    (t) => t.value == timeframeType
  )[0].range_end;

  if (timeframeType == "Date Range") {
    range_start = rangeStartValue;
    range_end = rangeEndValue;

    // Guard against an inverted range (start/end inputs filled in the opposite
    // order), which would otherwise build an empty day range and return zeros.
    if (new Date(range_start) > new Date(range_end)) {
      [range_start, range_end] = [range_end, range_start];
    }

    group_by = ReportsHelpers.getGroupBy(null, range_start, range_end);
  }

  const comparisonRange = ReportsHelpers.getComparisonRange(
    range_start,
    range_end
  );
  const isCustomTimeframe = timeframeType === "Date Range";
  const timeframeRangeDays = (() => {
    const start = new Date(range_start);
    const end = new Date(range_end);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return 0;
    }

    return (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
  })();
  const showTimeframeLimitAlert = isCustomTimeframe || timeframeRangeDays > 90;

  const syncTimeframeToInsightsUrl = (
    nextTimeframe,
    nextRangeStart,
    nextRangeEnd
  ) => {
    if (!props.syncInsightsTimeframe || !props.insightsUrl) {
      return;
    }

    let syncedStart = nextRangeStart;
    let syncedEnd = nextRangeEnd;

    if (
      nextTimeframe === "Date Range" &&
      new Date(syncedStart) > new Date(syncedEnd)
    ) {
      [syncedStart, syncedEnd] = [syncedEnd, syncedStart];
    }

    syncInsightsTimeframeToUrl(
      props.insightsUrl,
      nextTimeframe,
      syncedStart,
      syncedEnd
    );
  };

  useEffect(() => {
    if (!props.syncInsightsTimeframe || !props.insightsUrl) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get("timeframe")) {
      return;
    }

    syncTimeframeToInsightsUrl(
      initialTimeframeLabel,
      initialTimeframeLabel === "Date Range"
        ? initialStartDate
        : rangeStartValue,
      initialTimeframeLabel === "Date Range" ? initialEndDate : rangeEndValue
    );
  }, []);

  useEffect(() => {
    if (!showingIntegrationSubscribe || !last30DaysTimeframe) {
      setConvertFlowCompareCount(null);
      return undefined;
    }

    let cancelled = false;
    setConvertFlowCompareCount(null);

    const payload = {
      report_type: "distinct_count",
      report: {
        website_id: reportWebsiteId,
        event_type: "Contact Created",
        range_start: last30DaysTimeframe.range_start,
        range_end: last30DaysTimeframe.range_end,
      },
    };

    fetch(`${reportsPath}?${$.param(payload)}`)
      .then((response) => response.json())
      .then((json) => {
        if (!cancelled) {
          setConvertFlowCompareCount(Number(json) || 0);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setConvertFlowCompareCount(0);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    showingIntegrationSubscribe,
    subscribeSource,
    reportWebsiteId,
    reportsPath,
    last30DaysTimeframe?.range_start,
    last30DaysTimeframe?.range_end,
  ]);

  const handleTimeframeChange = (event) => {
    const value = event.target.value;
    const option = props.timeframeTypes.find((entry) => entry.value === value);
    const nextRangeStart =
      value === "Date Range" ? rangeStartValue : option?.range_start;
    const nextRangeEnd =
      value === "Date Range" ? rangeEndValue : option?.range_end;

    setTimeframeType(value);
    syncTimeframeToInsightsUrl(value, nextRangeStart, nextRangeEnd);
  };

  const handleRangeStartChange = (event) => {
    const value = utcDayStart(event.target.value);
    if (!value) return;
    setRangeStartValue(value);
    syncTimeframeToInsightsUrl("Date Range", value, rangeEndValue);
  };

  const handleRangeEndChange = (event) => {
    const value = utcDayEnd(event.target.value);
    if (!value) return;
    setRangeEndValue(value);
    syncTimeframeToInsightsUrl("Date Range", rangeStartValue, value);
  };

  const viewsReport = (
    <div className="col-xs-2 center no-padding-sides">
      <ReportCount
        label="Total Views"
        tooltip="Total count of all views of all campaigns. Viewing a campaign 2 times counts as 2 views. Viewing 2 funnel steps count as 2 views."
        path={reportsPath}
        payload={{
          report_type: "count",
          report: {
            website_id: reportWebsiteId,
            event_type: "View",
            range_start: range_start,
            range_end: range_end,
            variant_id: null,
            broadcast_id: null,
          },
        }}
        comparison_payload={
          comparisonRange
            ? {
                report_type: "count",
                report: {
                  website_id: reportWebsiteId,
                  event_type: "View",
                  range_start: comparisonRange.range_start,
                  range_end: comparisonRange.range_end,
                  variant_id: null,
                  broadcast_id: null,
                },
              }
            : null
        }
      />
    </div>
  );

  const uniqueViewsReport = (
    <div className="col-xs-2 center no-padding-sides">
      <ReportCount
        id={`website-${website.id}-views`}
        label="Unique Viewers"
        tooltip="Number of unique visitors who viewed a campaign"
        path={reportsPath}
        payload={{
          report_type: "distinct_count",
          report: {
            website_id: reportWebsiteId,
            event_type: "View",
            range_start: range_start,
            range_end: range_end,
            variant_id: null,
            broadcast_id: null,
            field: null,
          },
        }}
        comparison_payload={
          comparisonRange
            ? {
                report_type: "distinct_count",
                report: {
                  website_id: reportWebsiteId,
                  event_type: "View",
                  range_start: comparisonRange.range_start,
                  range_end: comparisonRange.range_end,
                  variant_id: null,
                  broadcast_id: null,
                  field: null,
                },
              }
            : null
        }
        callback={() => {
          updateConversionRate(
            `#website-${website.id}-views`,
            `#website-${website.id}-conversions`,
            `#website-${website.id}-conversion-rate`,
            { comparison_expected: !!comparisonRange }
          );
          updateConversionRate(
            `#website-${website.id}-views`,
            `#website-${website.id}-contacts`,
            `#website-${website.id}-subscribe-rate`,
            { comparison_expected: !!comparisonRange }
          );

          if (metric == "add_to_cart") {
            updateConversionRate(
              `#website-${website.id}-views`,
              `#website-${website.id}-added-to-cart`,
              `#website-${website.id}-add-to-cart-rate`,
              { comparison_expected: !!comparisonRange }
            );
          }

          if (["add_to_cart", "orders", "revenue"].includes(metric)) {
            updateConversionRate(
              `#website-${website.id}-views`,
              `#website-${website.id}-orders`,
              `#website-${website.id}-order-rate`,
              { comparison_expected: !!comparisonRange }
            );
          }

          if (metric == "bookings") {
            updateConversionRate(
              `#website-${website.id}-views`,
              `#website-${website.id}-bookings`,
              `#website-${website.id}-booking-rate`,
              { comparison_expected: !!comparisonRange }
            );
          }

          if (metric == "custom_goals") {
            updateConversionRate(
              `#website-${website.id}-views`,
              `#website-${website.id}-custom-goals`,
              `#website-${website.id}-custom-goal-rate`,
              { comparison_expected: !!comparisonRange }
            );
          }
        }}
      />
    </div>
  );

  const conversionsReport = (
    <div className="col-xs-2 center no-padding-sides">
      <ReportCount
        id={`website-${website.id}-conversions`}
        label="Unique Convs."
        tooltip="Number of unique viewers who clicked a campaign"
        path={reportsPath}
        payload={{
          report_type: "distinct_count",
          report: {
            website_id: reportWebsiteId,
            event_type: "Complete",
            variant_id: null,
            broadcast_id: null,
            range_start: range_start,
            range_end: range_end,
            field: null,
          },
        }}
        comparison_payload={
          comparisonRange
            ? {
                report_type: "distinct_count",
                report: {
                  website_id: reportWebsiteId,
                  event_type: "Complete",
                  variant_id: null,
                  broadcast_id: null,
                  range_start: comparisonRange.range_start,
                  range_end: comparisonRange.range_end,
                  field: null,
                },
              }
            : null
        }
        callback={() => {
          updateConversionRate(
            `#website-${website.id}-views`,
            `#website-${website.id}-conversions`,
            `#website-${website.id}-conversion-rate`,
            { comparison_expected: !!comparisonRange }
          );
        }}
      />
    </div>
  );

  const conversionRateReport = (
    <div className="col-xs-2 center no-padding-sides">
      <div className="row hard-center">
        <div className="row hard-center">
          <span className="field-title">Conv. Rate</span>
          <Tooltip
            tooltip={"Unique viewers compared to unique conversions"}
            inline={true}
          />
        </div>
        <div className="row hard-center">
          <h4
            id={`website-${website.id}-conversion-rate`}
            className="report-count bold"
            style={{ margin: "5px 0px 0px" }}
          >
            0.00%
          </h4>
          <span
            className="inline-flex items-center px-2 py-1 ml-2 rounded text-base font-medium conversion-rate-comparison-badge conversion-rate-comparison-loading bg-gray-100 text-gray-800"
            style={{ minWidth: "80px" }}
          >
            <span
              className="cf-loading-spinner reports-spinner"
              style={{ width: "12px", height: "12px", marginRight: "4px" }}
            ></span>
          </span>
        </div>
      </div>
    </div>
  );

  const completionsReport = (
    <div className="col-xs-2 center no-padding-sides">
      <ReportCount
        id={`website-${website.id}-completions`}
        label="Unique Comps."
        tooltip="Number of unique viewers who reached completion"
        path={reportsPath}
        payload={{
          report_type: "distinct_count",
          report: {
            website_id: reportWebsiteId,
            event_type: "Completion",
            variant_id: null,
            broadcast_id: null,
            range_start: range_start,
            range_end: range_end,
            field: null,
          },
        }}
        comparison_payload={
          comparisonRange
            ? {
                report_type: "distinct_count",
                report: {
                  website_id: reportWebsiteId,
                  event_type: "Completion",
                  variant_id: null,
                  broadcast_id: null,
                  range_start: comparisonRange.range_start,
                  range_end: comparisonRange.range_end,
                  field: null,
                },
              }
            : null
        }
        callback={() => {
          updateConversionRate(
            `#website-${website.id}-views`,
            `#website-${website.id}-completions`,
            `#website-${website.id}-completion-rate`,
            { comparison_expected: !!comparisonRange }
          );
        }}
      />
    </div>
  );

  const completionRateReport = (
    <div className="col-xs-2 center no-padding-sides">
      <div className="row hard-center">
        <div className="row hard-center">
          <span className="field-title">Comp. Rate</span>
          <Tooltip
            tooltip={"Unique viewers compared to unique completions"}
            inline={true}
          />
        </div>
        <div className="row hard-center">
          <h4
            id={`website-${website.id}-completion-rate`}
            className="report-count bold"
            style={{ margin: "5px 0px 0px" }}
          >
            0.00%
          </h4>
          <span
            className="inline-flex items-center px-2 py-1 ml-2 rounded text-base font-medium conversion-rate-comparison-badge conversion-rate-comparison-loading bg-gray-100 text-gray-800"
            style={{ minWidth: "80px" }}
          >
            <span
              className="cf-loading-spinner reports-spinner"
              style={{ width: "12px", height: "12px", marginRight: "4px" }}
            ></span>
          </span>
        </div>
      </div>
    </div>
  );

  const ordersReport = (
    <div className="col-xs-2 center no-padding-sides">
      <ReportCount
        id={`website-${website.id}-orders`}
        label="Orders"
        tooltip="Total orders that converted and then purchased within the configured attribution window"
        path={reportsPath}
        payload={{
          report_type: "count",
          report: {
            website_id: checkoutsReportWebsiteId,
            event_type: "Order",
            contactable_type: "Cta",
            contactable_id: null,
            range_start: range_start,
            range_end: range_end,
          },
        }}
        comparison_payload={
          comparisonRange
            ? {
                report_type: "count",
                report: {
                  website_id: checkoutsReportWebsiteId,
                  event_type: "Order",
                  contactable_type: "Cta",
                  contactable_id: null,
                  range_start: comparisonRange.range_start,
                  range_end: comparisonRange.range_end,
                },
              }
            : null
        }
        callback={() => {
          updateConversionRate(
            `#website-${website.id}-views`,
            `#website-${website.id}-orders`,
            `#website-${website.id}-order-rate`,
            { comparison_expected: !!comparisonRange }
          );
        }}
      />
    </div>
  );

  const ordersRateReport = (
    <div className="col-xs-2 center no-padding-sides">
      <div className="row hard-center">
        <div className="row hard-center">
          <span className="field-title">Purchase Rate</span>
          <Tooltip
            tooltip={
              "Percentage of viewers who converted and then purchased within the configured attribution window"
            }
            inline={true}
          />
        </div>
        <div className="row hard-center">
          <h4
            id={`website-${website.id}-order-rate`}
            className="report-count bold"
            style={{ margin: "5px 0px 0px" }}
          >
            0.00%
          </h4>
          <span
            className="inline-flex items-center px-2 py-1 ml-2 rounded text-base font-medium conversion-rate-comparison-badge conversion-rate-comparison-loading bg-gray-100 text-gray-800"
            style={{ minWidth: "80px" }}
          >
            <span
              className="cf-loading-spinner reports-spinner"
              style={{ width: "12px", height: "12px", marginRight: "4px" }}
            ></span>
          </span>
        </div>
      </div>
    </div>
  );

  const revenueReport = (
    <div className="col-xs-2 center no-padding-sides">
      <ReportCount
        id={`website-${website.id}-revenue`}
        label="Revenue"
        tooltip="Revenue from orders that converted with a campaign and then purchased within the configured attribution window"
        path={reportsPath}
        payload={{
          report_type: "revenue",
          report: {
            website_id: checkoutsReportWebsiteId,
            event_type: "Order",
            contactable_type: "Cta",
            contactable_id: null,
            range_start: range_start,
            range_end: range_end,
          },
        }}
        comparison_payload={
          comparisonRange
            ? {
                report_type: "revenue",
                report: {
                  website_id: checkoutsReportWebsiteId,
                  event_type: "Order",
                  contactable_type: "Cta",
                  contactable_id: null,
                  variant_id: null,
                  step_id: null,
                  element_id: null,
                  range_start: comparisonRange.range_start,
                  range_end: comparisonRange.range_end,
                  field: null,
                  broadcast_id: null,
                },
              }
            : null
        }
        currency={props.currency}
      />
    </div>
  );

  const bookingsReport = (
    <div className="col-xs-2 center no-padding-sides">
      <ReportCount
        id={`website-${website.id}-bookings`}
        label="Bookings"
        tooltip="Unique visitors who booked a meeting through a scheduler element"
        path={reportsPath}
        payload={{
          report_type: "distinct_count",
          report: {
            website_id: bookingsReportWebsiteId,
            event_type: "Booking",
            contactable_type: null,
            contactable_id: null,
            range_start: range_start,
            range_end: range_end,
            variant_id: null,
            broadcast_id: null,
            field: null,
          },
        }}
        comparison_payload={
          comparisonRange
            ? {
                report_type: "distinct_count",
                report: {
                  website_id: bookingsReportWebsiteId,
                  event_type: "Booking",
                  contactable_type: null,
                  contactable_id: null,
                  range_start: comparisonRange.range_start,
                  range_end: comparisonRange.range_end,
                  variant_id: null,
                  broadcast_id: null,
                  field: null,
                },
              }
            : null
        }
        callback={() => {
          updateConversionRate(
            `#website-${website.id}-views`,
            `#website-${website.id}-bookings`,
            `#website-${website.id}-booking-rate`,
            { comparison_expected: !!comparisonRange }
          );
        }}
      />
    </div>
  );

  const bookingRateReport = (
    <div className="col-xs-2 center no-padding-sides">
      <div className="row hard-center">
        <div className="row hard-center">
          <span className="field-title">Booking Rate</span>
          <Tooltip
            tooltip={"Unique viewers compared to unique bookings"}
            inline={true}
          />
        </div>
        <div className="row hard-center">
          <h4
            id={`website-${website.id}-booking-rate`}
            className="report-count bold"
            style={{ margin: "5px 0px 0px" }}
          >
            0.00%
          </h4>
        </div>
      </div>
    </div>
  );

  const customGoalsReport = (
    <div className="col-xs-2 center no-padding-sides">
      <ReportCount
        id={`website-${website.id}-custom-goals`}
        label={goals.customGoalLabel}
        tooltip={`Unique visitors who completed ${goals.customGoalLabel}`}
        path={reportsPath}
        payload={{
          report_type: "distinct_count",
          report: {
            website_id: customGoalReportWebsiteId,
            event_type: "Custom Goal",
            contactable_type: null,
            contactable_id: null,
            range_start: range_start,
            range_end: range_end,
            variant_id: null,
            broadcast_id: null,
            field: null,
          },
        }}
        comparison_payload={
          comparisonRange
            ? {
                report_type: "distinct_count",
                report: {
                  website_id: customGoalReportWebsiteId,
                  event_type: "Custom Goal",
                  contactable_type: null,
                  contactable_id: null,
                  range_start: comparisonRange.range_start,
                  range_end: comparisonRange.range_end,
                  variant_id: null,
                  broadcast_id: null,
                  field: null,
                },
              }
            : null
        }
        callback={() => {
          updateConversionRate(
            `#website-${website.id}-views`,
            `#website-${website.id}-custom-goals`,
            `#website-${website.id}-custom-goal-rate`,
            { comparison_expected: !!comparisonRange }
          );
        }}
      />
    </div>
  );

  const customGoalRateReport = (
    <div className="col-xs-2 center no-padding-sides">
      <div className="row hard-center">
        <div className="row hard-center">
          <span className="field-title">{goals.customGoalLabel} Rate</span>
          <Tooltip
            tooltip={`Unique viewers compared to unique ${goals.customGoalLabel}`}
            inline={true}
          />
        </div>
        <div className="row hard-center">
          <h4
            id={`website-${website.id}-custom-goal-rate`}
            className="report-count bold"
            style={{ margin: "5px 0px 0px" }}
          >
            0.00%
          </h4>
        </div>
      </div>
    </div>
  );

  const aovReport = (
    <div className="col-xs-2 center no-padding-sides">
      <div className="row hard-center">
        <div
          data-toggle="tooltip"
          data-placement="left"
          title={
            "Average revenue of those who converted with a campaign and then purchased within the configured attribution window"
          }
          className="row hard-center"
        >
          <span className="field-title">AOV</span>
          <Tooltip
            tooltip={
              "Average revenue of those who converted with a campaign and then purchased within the configured attribution window"
            }
            inline={true}
          />
        </div>
        <div className="row hard-center gap-2">
          <h4 className="report-count bold" style={{ margin: "5px 0px 0px" }}>
            {props.currency}
            <span id={`website-${website.id}-aov`}>
              {currentAOV.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </h4>
          {aovPercentageChange !== null && (
            <span
              className={`inline-flex items-center px-2 py-1 rounded text-base font-medium ${
                aovPercentageChange > 0
                  ? "bg-green-100 text-green-800"
                  : aovPercentageChange < 0
                  ? "bg-red-100 text-red-800"
                  : "bg-gray-100 text-gray-800"
              }`}
              onMouseEnter={() => {
                setAovLiftIsHovered(true);
              }}
              onMouseLeave={() => {
                setAovLiftIsHovered(false);
              }}
            >
              {aovPercentageChange > 0 ? (
                <svg
                  className="w-4 h-4 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  style={{ transform: "rotate(-45deg)" }}
                >
                  <path
                    fillRule="evenodd"
                    d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : aovPercentageChange < 0 ? (
                <svg
                  className="w-4 h-4 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  style={{ transform: "rotate(45deg)" }}
                >
                  <path
                    fillRule="evenodd"
                    d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : null}
              {Math.abs(aovPercentageChange)}%
              <span
                className="ml-1"
                style={{ display: aovLiftIsHovered ? "inline-block" : "none" }}
              >
                vs last period
              </span>
            </span>
          )}
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    $(rangeStartInput.current)
      .datetimepicker({
        defaultDate: new Date().setHours(0, 0, 0, 0),
        ignoreReadonly: true,
        allowInputToggle: true,
      })
      .off("dp.change")
      .on("dp.change", function (e) {
        const value = utcDayStart(pickerDate(e));
        if (!value) return;
        setRangeStartValue(value);
        syncTimeframeToInsightsUrl("Date Range", value, rangeEndValue);
      });

    $(rangeEndInput.current)
      .datetimepicker({
        defaultDate: new Date().setHours(23, 59, 59, 999),
        ignoreReadonly: true,
        allowInputToggle: true,
      })
      .off("dp.change")
      .on("dp.change", function (e) {
        const value = utcDayEnd(pickerDate(e));
        if (!value) return;
        setRangeEndValue(value);
        syncTimeframeToInsightsUrl("Date Range", rangeStartValue, value);
      });
  }, [metric, timeframeType, rangeStartValue, rangeEndValue]);

  useEffect(() => {
    const calculateAOV = () => {
      const ordersSpan = $(`#website-${website.id}-orders`);
      const revenueSpan = $(`#website-${website.id}-revenue`);

      if (
        ordersSpan.attr("data-loading") == "false" &&
        revenueSpan.attr("data-loading") == "false"
      ) {
        const orders = readReportCount(ordersSpan);
        const revenue = readReportCount(revenueSpan);
        const aov = orders && revenue ? revenue / orders : 0;
        setCurrentAOV(aov);

        // Get comparison values from data attributes set by ReportCount
        const ordersComparisonCount = parseInt(
          ordersSpan.attr("data-comparison-count") || "0"
        );
        const revenueComparisonCount = parseFloat(
          revenueSpan.attr("data-comparison-count") || "0"
        );
        const compAov =
          ordersComparisonCount && revenueComparisonCount
            ? revenueComparisonCount / ordersComparisonCount
            : 0;
        setComparisonAOV(compAov);
      }
    };

    const intervalId = setInterval(calculateAOV, 500);
    return () => clearInterval(intervalId);
  }, [website.id, timeframeType, rangeStartValue, rangeEndValue]);

  useEffect(() => {
    if (comparisonAOV !== null && currentAOV !== null && comparisonAOV !== 0) {
      const change = ((currentAOV - comparisonAOV) / comparisonAOV) * 100;
      setAovPercentageChange(Math.round(change));
    } else if (comparisonAOV === 0 && currentAOV > 0) {
      setAovPercentageChange(100);
    } else if (comparisonAOV !== null && currentAOV !== null) {
      setAovPercentageChange(0);
    }
  }, [currentAOV, comparisonAOV]);

  return (
    <React.Fragment>
      <div className="row hard-center smooth WebsiteConversionReport">
        {showTimeframeLimitAlert && (
          <div className="row hard-center px-[15px]">
            <div className="col-xs-12 some-vertical-space border-solid border-[1px] border-black text-black pt-6 pb-2">
              <p className="smooth semibold">
                {scopeLabel} timeframe reports for subscribes/contacts and
                orders/revenue/AOV are limited to 2026+ with improved accuracy.
              </p>
            </div>
          </div>
        )}
        <div className="row hard-center some-vertical-space">
          <div className="col-xs-3 no-padding-right">
            <h4 className="bold mb-0">{props.title || "Funnel Performance"}</h4>
            <p className="insight-description light text-xl mb-0 mt-1">
              How ConvertFlow is performing
            </p>
          </div>
          <div className="col-xs-9 no-padding-left pt-3">
            <div className="flex flex-row items-center justify-end gap-2 flex-nowrap">
              {metric !== "revenue" && !showingIntegrationSubscribe && (
                <div
                  className="btn-group smooth shrink-0 bg-white rounded-md !h-[34px]"
                  role="group"
                >
                  <button
                    type="button"
                    className={`bg-white text-slate-500 rounded-md border-solid border-[1px] border-white box-border !h-[34px] !p-0 inline-flex items-center justify-center ${
                      analysisType == "funnel" && metric !== "viewers"
                        ? "!border-slate-200"
                        : metric == "viewers"
                        ? "disabled"
                        : ""
                    }`}
                    data-toggle="tooltip"
                    data-placement="bottom"
                    title={`Funnel report`}
                    onClick={() => {
                      if (metric !== "viewers") {
                        setAnalysisType("funnel");
                      }
                    }}
                    style={{ width: "34px" }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                    >
                      <path
                        d="M4.84615 9H13.1538M3 5.25H15M7.61538 12.75H10.3846"
                        stroke="black"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className={`bg-white text-slate-500 rounded-md border-solid border-[1px] border-white box-border !h-[34px] !p-0 inline-flex items-center justify-center ${
                      analysisType == "counts" || metric == "viewers"
                        ? "!border-slate-200"
                        : ""
                    }`}
                    data-toggle="tooltip"
                    data-placement="bottom"
                    title={`Counts over time`}
                    onClick={() => {
                      setAnalysisType("counts");
                    }}
                    style={{ width: "34px" }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                    >
                      <path
                        d="M14.4004 14.3996V12.5996M9.00039 14.3996V8.09961M3.60039 14.3996V3.59961"
                        stroke="#7F8493"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  <button
                    type="button"
                    className={`bg-white text-slate-500 rounded-md border-solid border-[1px] border-white box-border !h-[34px] !p-0 inline-flex items-center justify-center ${
                      analysisType == "performance" && metric !== "viewers"
                        ? "!border-slate-200"
                        : metric == "viewers"
                        ? "disabled"
                        : ""
                    }`}
                    data-toggle="tooltip"
                    data-placement="bottom"
                    title={`Rates over time`}
                    onClick={() => {
                      if (metric !== "viewers") {
                        setAnalysisType("performance");
                      }
                    }}
                    style={{ width: "34px" }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                    >
                      <path
                        d="M12.6002 7.1998L9.9002 9.8998L7.6502 7.6498L4.9502 10.3498M14.4002 16.1998C15.3943 16.1998 16.2002 15.3939 16.2002 14.3998V3.5998C16.2002 2.60569 15.3943 1.7998 14.4002 1.7998H3.6002C2.60608 1.7998 1.8002 2.60569 1.8002 3.5998V14.3998C1.8002 15.3939 2.60608 16.1998 3.6002 16.1998H14.4002Z"
                        stroke="#7F8493"
                        strokeWidth="1.3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              )}
              {goals.checkouts && metric == "revenue" && (
                <select
                  className="text-field shrink-0"
                  style={{ width: "120px" }}
                  onChange={(event) => setRevenueType(event.target.value)}
                  value={revenueType}
                >
                  <option value={"attributed"}>Attributed</option>
                  <option value={"total"}>Total</option>
                </select>
              )}
              {metric == "subscribes" && contactSubscribeReports.length > 0 && (
                <select
                  className="text-field shrink-0"
                  style={{ width: "160px" }}
                  onChange={(event) => setSubscribeSource(event.target.value)}
                  value={subscribeSource}
                >
                  <option value={"convertflow"}>ConvertFlow</option>
                  {contactSubscribeReports.map((report) => (
                    <option
                      value={report.type}
                      key={`subscribe-source-${report.type}`}
                    >
                      {report.type}
                    </option>
                  ))}
                </select>
              )}
              {timeframeType == "Date Range" &&
                !showingIntegrationSubscribe && (
                  <div
                    className="input-group date range-start shrink-0"
                    ref={rangeStartInput}
                    style={{ width: "130px" }}
                  >
                    <input
                      type="text"
                      className="text-field cursor-default !h-[34px] !py-[6px] !px-[10px] !leading-[20px] box-border"
                      placeholder="Start"
                      value={utcDayLabel(rangeStartValue)}
                      style={{ borderRadius: "3px 0px 0px 3px" }}
                      onChange={handleRangeStartChange}
                      readOnly
                    />
                    <span className="input-group-addon !border-slate-200 !bg-white !text-black !h-[34px] !py-0 !px-[8px] !leading-[32px] box-border">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 18 18"
                        fill="none"
                      >
                        <path
                          d="M5.35714 5.92632H12.5859M4.86384 1.82812V3.05773M12.9844 1.82812V3.05758M15.375 6.05758L15.375 13.1719C15.375 14.8287 14.0319 16.1719 12.375 16.1719H5.625C3.96815 16.1719 2.625 14.8287 2.625 13.1719V6.05758C2.625 4.40073 3.96815 3.05758 5.625 3.05758H12.375C14.0319 3.05758 15.375 4.40073 15.375 6.05758Z"
                          stroke="black"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                )}
              {timeframeType == "Date Range" &&
                !showingIntegrationSubscribe && (
                  <div
                    className="input-group date range-end shrink-0"
                    ref={rangeEndInput}
                    style={{ width: "130px" }}
                  >
                    <input
                      type="text"
                      className="text-field cursor-default !h-[34px] !py-[6px] !px-[10px] !leading-[20px] box-border"
                      placeholder="End"
                      value={utcDayLabel(rangeEndValue)}
                      style={{ borderRadius: "3px 0px 0px 3px" }}
                      onChange={handleRangeEndChange}
                      readOnly
                    />
                    <span className="input-group-addon !border-slate-200 !bg-white !text-black !h-[34px] !py-0 !px-[8px] !leading-[32px] box-border">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 18 18"
                        fill="none"
                      >
                        <path
                          d="M5.35714 5.92632H12.5859M4.86384 1.82812V3.05773M12.9844 1.82812V3.05758M15.375 6.05758L15.375 13.1719C15.375 14.8287 14.0319 16.1719 12.375 16.1719H5.625C3.96815 16.1719 2.625 14.8287 2.625 13.1719V6.05758C2.625 4.40073 3.96815 3.05758 5.625 3.05758H12.375C14.0319 3.05758 15.375 4.40073 15.375 6.05758Z"
                          stroke="black"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                )}
              {false == (metric == "revenue" && revenueType == "total") &&
                !showingIntegrationSubscribe && (
                  <div className="timeframe-date-selector !float-none !mt-0 shrink-0">
                    <select
                      className="text-field !h-[34px] !py-[6px] !px-[10px] !leading-[20px] box-border"
                      style={{ width: "100%", float: "none" }}
                      onChange={handleTimeframeChange}
                      name="timeframe"
                      value={timeframeType}
                    >
                      {props.timeframeTypes.map((option) => (
                        <option
                          value={option.value}
                          key={`timeframe-option-${option.value}`}
                        >
                          {[
                            "Today",
                            "Yesterday",
                            "This Week",
                            "This Month",
                            "This Quarter",
                            "This Year",
                          ].includes(option.value)
                            ? `${option.value} (Timezone: UTC)`
                            : option.value == "Date Range"
                            ? "Date Range (UTC)"
                            : option.value}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              {props.exportTriggerUrl && (
                <ExportTriggerButton
                  exportUrl={props.exportTriggerUrl}
                  exportFormat={props.exportFormat || "csv"}
                  style={{ flexShrink: 0 }}
                />
              )}
            </div>
          </div>
        </div>
        <div className="row hard-center">
          <ul
            className="nav nav-tabs !flex reports-tabs border-solid border-0 !border-slate-200 border-b-[1px]"
            role="tab"
            style={{
              borderBottom: "1px solid #E9EBf1",
            }}
          >
            <li
              role="presentation"
              className={`flex-1 text-center ${
                metric == "viewers" ? "active" : ""
              }`}
            >
              <a
                className="text-xl semibold metric-tab"
                data-metric="viewers"
                onClick={() => setMetric("viewers")}
              >
                Viewers
              </a>
            </li>
            <li
              className={`flex-1 text-center ${
                metric == "conversions" ? "active" : ""
              }`}
            >
              <a
                className="text-xl semibold metric-tab"
                data-metric="conversions"
                onClick={() => setMetric("conversions")}
              >
                Conversions
              </a>
            </li>
            <li
              className={`flex-1 text-center ${
                metric == "completions" ? "active" : ""
              }`}
            >
              <a
                className="text-xl semibold metric-tab"
                data-metric="completions"
                onClick={() => setMetric("completions")}
              >
                Completions
              </a>
            </li>
            <li
              className={`flex-1 text-center ${
                metric == "subscribes" ? "active" : ""
              }`}
            >
              <a
                className="text-xl semibold metric-tab"
                data-metric="subscribes"
                onClick={() => setMetric("subscribes")}
              >
                Contacts
              </a>
            </li>
            {goals.checkouts && (
              <li
                className={`flex-1 text-center ${
                  metric == "add_to_cart" ? "active" : ""
                }`}
              >
                <a
                  className="text-xl semibold metric-tab"
                  data-metric="add_to_cart"
                  onClick={() => setMetric("add_to_cart")}
                >
                  Add To Cart
                </a>
              </li>
            )}
            {goals.checkouts && (
              <li
                className={`flex-1 text-center ${
                  metric == "orders" ? "active" : ""
                }`}
              >
                <a
                  className="text-xl semibold metric-tab"
                  data-metric="orders"
                  onClick={() => setMetric("orders")}
                >
                  Orders
                </a>
              </li>
            )}
            {goals.checkouts && (
              <li
                className={`flex-1 text-center ${
                  metric == "revenue" ? "active" : ""
                }`}
              >
                <a
                  className="text-xl semibold metric-tab"
                  data-metric="revenue"
                  onClick={() => setMetric("revenue")}
                >
                  Revenue
                </a>
              </li>
            )}
            {goals.bookings && (
              <li
                className={`flex-1 text-center ${
                  metric == "bookings" ? "active" : ""
                }`}
              >
                <a
                  className="text-xl semibold metric-tab"
                  data-metric="bookings"
                  onClick={() => setMetric("bookings")}
                >
                  Bookings
                </a>
              </li>
            )}
            {goals.custom && (
              <li
                className={`flex-1 text-center ${
                  metric == "custom_goals" ? "active" : ""
                }`}
              >
                <a
                  className="text-xl semibold metric-tab"
                  data-metric="custom_goals"
                  onClick={() => setMetric("custom_goals")}
                >
                  {goals.customGoalLabel}
                </a>
              </li>
            )}
          </ul>
        </div>

        {metric == "viewers" && (
          <React.Fragment>
            <div
              className="row hard-center"
              style={{ borderBottom: "1px solid rgb(233, 235, 241)" }}
            >
              <div
                className="col-xs-12 vertical-space center"
                style={{ marginBottom: "0px" }}
              >
                <span className="light text-xl ellipsis">
                  See how many people have viewed your campaigns.
                </span>
              </div>
              <div className="col-xs-12 some-vertical-space">
                <ReportChart
                  path={reportsPath}
                  payload={{
                    report_type: "distinct_count",
                    group_by: group_by,
                    report: {
                      website_id: reportWebsiteId,
                      event_type: "View",
                      range_start: range_start,
                      range_end: range_end,
                      variant_id: null,
                      field: null,
                      broadcast_id: null,
                      start_at_first:
                        timeframeType == "All Time" ? true : false,
                    },
                  }}
                  height="200px"
                />
              </div>
            </div>
            <div className="row hard-center vertical-space report-column-wrapper">
              {viewsReport}
              {uniqueViewsReport}
              {conversionsReport}
              {conversionRateReport}
            </div>
          </React.Fragment>
        )}
        {metric == "conversions" && (
          <React.Fragment>
            <div
              className="row hard-center"
              style={{ borderBottom: "1px solid rgb(233, 235, 241)" }}
            >
              <div
                className="col-xs-12 vertical-space center"
                style={{ marginBottom: "0px" }}
              >
                <span className="light text-xl ellipsis">
                  See how many viewers have clicked a button, survey or form in
                  a campaign.
                </span>
              </div>
              <div className="col-xs-12 some-vertical-space">
                {analysisType == "counts" && (
                  <ReportChart
                    path={reportsPath}
                    payload={{
                      report_type: "distinct_count",
                      group_by: group_by,
                      report: {
                        website_id: reportWebsiteId,
                        event_type: "Complete",
                        range_start: range_start,
                        range_end: range_end,
                        variant_id: null,
                        broadcast_id: null,
                        field: null,
                        start_at_first:
                          timeframeType == "All Time" ? true : false,
                      },
                    }}
                    height="200px"
                  />
                )}
                {analysisType == "funnel" && (
                  <ReportFunnelChart
                    reports={[
                      {
                        label: "Unique Viewers",
                        path: reportsPath,
                        payload: {
                          report_type: "distinct_count",
                          report: {
                            website_id: reportWebsiteId,
                            event_type: "View",
                            range_start: range_start,
                            range_end: range_end,
                            variant_id: null,
                            broadcast_id: null,
                            field: null,
                          },
                        },
                      },
                      {
                        label: "Unique Conversions",
                        path: reportsPath,
                        payload: {
                          report_type: "distinct_count",
                          report: {
                            website_id: reportWebsiteId,
                            event_type: "Complete",
                            range_start: range_start,
                            range_end: range_end,
                            variant_id: null,
                            broadcast_id: null,
                            field: null,
                          },
                        },
                      },
                    ]}
                    height="200px"
                  />
                )}
                {analysisType == "performance" && (
                  <ReportConversionRateChart
                    viewPath={reportsPath}
                    viewPayload={{
                      report_type: "distinct_count",
                      group_by: group_by,
                      report: {
                        website_id: reportWebsiteId,
                        event_type: "View",
                        range_start: range_start,
                        range_end: range_end,
                        variant_id: null,
                        broadcast_id: null,
                        field: null,
                        start_at_first:
                          timeframeType == "All Time" ? true : false,
                      },
                    }}
                    conversionPath={reportsPath}
                    conversionPayload={{
                      report_type: "distinct_count",
                      group_by: group_by,
                      report: {
                        website_id: reportWebsiteId,
                        event_type: "Complete",
                        range_start: range_start,
                        range_end: range_end,
                        variant_id: null,
                        broadcast_id: null,
                        field: null,
                        start_at_first:
                          timeframeType == "All Time" ? true : false,
                      },
                    }}
                    height="200px"
                  />
                )}
              </div>
            </div>
            <div className="row hard-center vertical-space report-column-wrapper">
              {uniqueViewsReport}
              {conversionsReport}
              {conversionRateReport}
            </div>
          </React.Fragment>
        )}
        {metric == "subscribes" && !showingIntegrationSubscribe && (
          <React.Fragment>
            <div
              className="row hard-center"
              style={{ borderBottom: "1px solid rgb(233, 235, 241)" }}
            >
              <div
                className="col-xs-12 vertical-space center"
                style={{ marginBottom: "0px" }}
              >
                <span className="light text-xl ellipsis">
                  See how many new contacts were created.{" "}
                  <a
                    href="https://help.convertflow.com/article/190-form-elements-how-to-build-forms-and-collect-contact-info"
                    target="_blank"
                  >
                    How to configure →
                  </a>{" "}
                </span>
              </div>
              <div className="col-xs-12 some-vertical-space">
                {analysisType == "funnel" && (
                  <ReportFunnelChart
                    reports={[
                      {
                        label: "Unique Viewers",
                        path: reportsPath,
                        payload: {
                          report_type: "distinct_count",
                          report: {
                            website_id: reportWebsiteId,
                            event_type: "View",
                            range_start: range_start,
                            range_end: range_end,
                            variant_id: null,
                            broadcast_id: null,
                            field: null,
                          },
                        },
                      },
                      {
                        label: "Unique Conversions",
                        path: reportsPath,
                        payload: {
                          report_type: "distinct_count",
                          report: {
                            website_id: reportWebsiteId,
                            event_type: "Complete",
                            range_start: range_start,
                            range_end: range_end,
                            variant_id: null,
                            broadcast_id: null,
                            field: null,
                          },
                        },
                      },
                      {
                        label: "New Contacts",
                        path: reportsPath,
                        payload: {
                          report_type: "distinct_count",
                          report: {
                            website_id: reportWebsiteId,
                            event_type: "Contact Created",
                            range_start: range_start,
                            range_end: range_end,
                          },
                        },
                      },
                    ]}
                    height="200px"
                  />
                )}
                {analysisType == "counts" && (
                  <ReportChart
                    path={reportsPath}
                    payload={{
                      report_type: "distinct_count",
                      group_by: group_by,
                      report: {
                        website_id: reportWebsiteId,
                        event_type: "Contact Created",
                        range_start: range_start,
                        range_end: range_end,
                        start_at_first:
                          timeframeType == "All Time" ? true : false,
                      },
                    }}
                    height="200px"
                  />
                )}
                {analysisType == "performance" && (
                  <ReportConversionRateChart
                    viewPath={reportsPath}
                    viewPayload={{
                      report_type: "distinct_count",
                      group_by: group_by,
                      report: {
                        website_id: reportWebsiteId,
                        event_type: "View",
                        range_start: range_start,
                        range_end: range_end,
                        variant_id: null,
                        broadcast_id: null,
                        field: null,
                        start_at_first:
                          timeframeType == "All Time" ? true : false,
                      },
                    }}
                    conversionPath={reportsPath}
                    conversionPayload={{
                      report_type: "distinct_count",
                      group_by: group_by,
                      report: {
                        website_id: reportWebsiteId,
                        event_type: "Contact Created",
                        range_start: range_start,
                        range_end: range_end,
                        start_at_first:
                          timeframeType == "All Time" ? true : false,
                      },
                    }}
                    height="200px"
                  />
                )}
              </div>
            </div>
            <div className="row hard-center vertical-space report-column-wrapper">
              {uniqueViewsReport}
              {conversionsReport}
              {conversionRateReport}
              <div className="col-xs-2 center no-padding-sides center">
                <ReportCount
                  id={`website-${website.id}-subscribes`}
                  label="New Contacts"
                  tooltip="New contacts created during the selected timeframe"
                  path={reportsPath}
                  loader={true}
                  payload={{
                    report_type: "distinct_count",
                    report: {
                      website_id: reportWebsiteId,
                      event_type: "Contact Created",
                      range_start: range_start,
                      range_end: range_end,
                    },
                  }}
                  comparison_payload={
                    comparisonRange
                      ? {
                          report_type: "distinct_count",
                          report: {
                            website_id: reportWebsiteId,
                            event_type: "Contact Created",
                            range_start: comparisonRange.range_start,
                            range_end: comparisonRange.range_end,
                          },
                        }
                      : null
                  }
                  callback={() => {
                    updateConversionRate(
                      `#website-${website.id}-views`,
                      `#website-${website.id}-subscribes`,
                      `#website-${website.id}-subscribe-rate`,
                      { comparison_expected: !!comparisonRange }
                    );
                  }}
                />
              </div>
              <div className="col-xs-2 center no-padding-sides">
                <div className="row hard-center">
                  <div className="row hard-center">
                    <span className="field-title">Subscribe Rate</span>
                    <Tooltip
                      tooltip={"Unique viewers compared to new contacts"}
                      inline={true}
                    />
                  </div>
                  <div className="row hard-center">
                    <h4
                      id={`website-${website.id}-subscribe-rate`}
                      className="report-count bold"
                      style={{ margin: "5px 0px 0px" }}
                    >
                      0.00%
                    </h4>
                    <span
                      className="inline-flex items-center px-2 py-1 ml-2 rounded text-base font-medium conversion-rate-comparison-badge conversion-rate-comparison-loading bg-gray-100 text-gray-800"
                      style={{ minWidth: "80px" }}
                    >
                      <span
                        className="cf-loading-spinner reports-spinner"
                        style={{
                          width: "12px",
                          height: "12px",
                          marginRight: "4px",
                        }}
                      ></span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </React.Fragment>
        )}
        {metric == "subscribes" && showingIntegrationSubscribe && (
          <React.Fragment>
            <div
              className="row hard-center"
              style={{ borderBottom: "1px solid rgb(233, 235, 241)" }}
            >
              <div
                className="col-xs-12 vertical-space center"
                style={{ marginBottom: "0px" }}
              >
                <span className="light text-xl ellipsis">
                  Compare ConvertFlow subscribe volume with{" "}
                  {selectedSubscribeReport.type} contacts created in the last 30
                  days.
                </span>
              </div>
              <div
                className="col-xs-12 some-vertical-space"
                data-testid="subscribe-volume-comparison-chart"
              >
                {convertFlowCompareCount === null ? (
                  <div className="center light text-xl">Loading...</div>
                ) : (
                  <ColumnChart
                    data={[
                      ["ConvertFlow", convertFlowCompareCount],
                      [selectedSubscribeChartLabel, selectedSubscribeCount],
                    ]}
                    thousands=","
                    height="200px"
                    library={{
                      plugins: {
                        tooltip: {
                          callbacks: {
                            label: function (context) {
                              return Math.round(context.raw).toLocaleString();
                            },
                          },
                        },
                      },
                    }}
                  />
                )}
              </div>
            </div>
            <div className="row hard-center vertical-space report-column-wrapper">
              <div className="col-xs-6 center no-padding-sides">
                <ReportCount
                  key={`convertflow-subscribes-compare-${convertFlowCompareCount}`}
                  id={`website-${website.id}-convertflow-subscribes-compare`}
                  label="ConvertFlow New Contacts"
                  tooltip="New ConvertFlow contacts created during the last 30 days"
                  count={convertFlowCompareCount ?? 0}
                />
              </div>
              <div className="col-xs-6 center no-padding-sides">
                <ReportCount
                  key={`integration-subscribes-${selectedSubscribeReport.type}`}
                  id={`website-${website.id}-integration-subscribes-${selectedSubscribeReport.type}`}
                  label={`${selectedSubscribeReport.type} New Contacts${
                    selectedSubscribeCapped ? "+" : ""
                  }`}
                  tooltip={
                    selectedSubscribeCapped
                      ? `At least ${selectedSubscribeCount.toLocaleString()} new contacts created in ${
                          selectedSubscribeReport.type
                        } over the last 30 days (count is capped)`
                      : `New contacts created in ${selectedSubscribeReport.type} over the last 30 days`
                  }
                  count={selectedSubscribeCount}
                />
              </div>
            </div>
          </React.Fragment>
        )}
        {metric == "completions" && (
          <React.Fragment>
            <div
              className="row hard-center"
              style={{ borderBottom: "1px solid rgb(233, 235, 241)" }}
            >
              <div
                className="col-xs-12 vertical-space center"
                style={{ marginBottom: "0px" }}
              >
                <span className="light text-xl ellipsis">
                  See how many viewers have been tracked as having completed
                  your campaigns.{" "}
                  <a
                    href="https://help.convertflow.com/article/203-reporting-on-how-your-cta-is-converting#completion"
                    target="_blank"
                  >
                    How to configure →
                  </a>{" "}
                </span>
              </div>
              <div className="col-xs-12 some-vertical-space">
                {analysisType == "counts" && (
                  <ReportChart
                    path={reportsPath}
                    payload={{
                      report_type: "distinct_count",
                      group_by: group_by,
                      report: {
                        website_id: reportWebsiteId,
                        event_type: "Completion",
                        range_start: range_start,
                        range_end: range_end,
                        variant_id: null,
                        broadcast_id: null,
                        field: null,
                        start_at_first:
                          timeframeType == "All Time" ? true : false,
                      },
                    }}
                    height="200px"
                  />
                )}
                {analysisType == "funnel" && (
                  <ReportFunnelChart
                    reports={[
                      {
                        label: "Unique Viewers",
                        path: reportsPath,
                        payload: {
                          report_type: "distinct_count",
                          report: {
                            website_id: reportWebsiteId,
                            event_type: "View",
                            range_start: range_start,
                            range_end: range_end,
                            variant_id: null,
                            broadcast_id: null,
                            field: null,
                          },
                        },
                      },
                      {
                        label: "Unique Conversions",
                        path: reportsPath,
                        payload: {
                          report_type: "distinct_count",
                          report: {
                            website_id: reportWebsiteId,
                            event_type: "Complete",
                            range_start: range_start,
                            range_end: range_end,
                            variant_id: null,
                            broadcast_id: null,
                            field: null,
                          },
                        },
                      },
                      {
                        label: "Unique Completions",
                        path: reportsPath,
                        payload: {
                          report_type: "distinct_count",
                          report: {
                            website_id: reportWebsiteId,
                            event_type: "Completion",
                            range_start: range_start,
                            range_end: range_end,
                            variant_id: null,
                            broadcast_id: null,
                            field: null,
                          },
                        },
                      },
                    ]}
                    height="200px"
                  />
                )}
                {analysisType == "performance" && (
                  <ReportConversionRateChart
                    viewPath={reportsPath}
                    viewPayload={{
                      report_type: "distinct_count",
                      group_by: group_by,
                      report: {
                        website_id: reportWebsiteId,
                        event_type: "View",
                        range_start: range_start,
                        range_end: range_end,
                        variant_id: null,
                        broadcast_id: null,
                        field: null,
                        start_at_first:
                          timeframeType == "All Time" ? true : false,
                      },
                    }}
                    conversionPath={reportsPath}
                    conversionPayload={{
                      report_type: "distinct_count",
                      group_by: group_by,
                      report: {
                        website_id: reportWebsiteId,
                        event_type: "Completion",
                        range_start: range_start,
                        range_end: range_end,
                        variant_id: null,
                        broadcast_id: null,
                        field: null,
                        start_at_first:
                          timeframeType == "All Time" ? true : false,
                      },
                    }}
                    height="200px"
                  />
                )}
              </div>
            </div>
            <div className="row hard-center vertical-space report-column-wrapper">
              {uniqueViewsReport}
              {conversionsReport}
              {conversionRateReport}
              {completionsReport}
              {completionRateReport}
            </div>
          </React.Fragment>
        )}
        {goals.checkouts && metric == "add_to_cart" && (
          <React.Fragment>
            <div
              className="row hard-center"
              style={{ borderBottom: "1px solid rgb(233, 235, 241)" }}
            >
              <div
                className="col-xs-12 vertical-space center"
                style={{ marginBottom: "0px" }}
              >
                <span className="light text-xl ellipsis">
                  See how many viewers have added a product to cart from
                  ConvertFlow campaigns.{" "}
                  <a
                    href="https://help.convertflow.com/article/347-products-elements-how-to-build-product-recommendations"
                    target="_blank"
                  >
                    How to configure →
                  </a>{" "}
                </span>
              </div>
              <div className="col-xs-12 some-vertical-space">
                {analysisType == "funnel" && (
                  <ReportFunnelChart
                    reports={[
                      {
                        label: "Unique Viewers",
                        path: reportsPath,
                        payload: {
                          report_type: "distinct_count",
                          report: {
                            website_id: reportWebsiteId,
                            event_type: "View",
                            variant_id: null,
                            broadcast_id: null,
                            range_start: range_start,
                            range_end: range_end,
                            field: null,
                          },
                        },
                      },
                      {
                        label: "Unique Conversions",
                        path: reportsPath,
                        payload: {
                          report_type: "distinct_count",
                          report: {
                            website_id: reportWebsiteId,
                            event_type: "Complete",
                            range_start: range_start,
                            range_end: range_end,
                            variant_id: null,
                            broadcast_id: null,
                            field: null,
                          },
                        },
                      },
                      {
                        label: "Added To Cart",
                        path: reportsPath,
                        payload: {
                          report_type: "distinct_count",
                          report: {
                            website_id: reportWebsiteId,
                            event_type: "Add To Cart",
                            range_start: range_start,
                            range_end: range_end,
                            variant_id: null,
                            broadcast_id: null,
                            field: null,
                          },
                        },
                      },
                      {
                        label: "Orders",
                        path: reportsPath,
                        payload: {
                          report_type: "count",
                          report: {
                            website_id: reportWebsiteId,
                            event_type: "Order",
                            contactable_type: "Cta",
                            contactable_id: null,
                            range_start: range_start,
                            range_end: range_end,
                          },
                        },
                      },
                    ]}
                    height="200px"
                  />
                )}
                {analysisType == "counts" && (
                  <ReportChart
                    path={reportsPath}
                    payload={{
                      report_type: "distinct_count",
                      group_by: group_by,
                      report: {
                        website_id: reportWebsiteId,
                        event_type: "Add To Cart",
                        range_start: range_start,
                        range_end: range_end,
                        variant_id: null,
                        broadcast_id: null,
                        field: null,
                        start_at_first:
                          timeframeType == "All Time" ? true : false,
                      },
                    }}
                    height="200px"
                  />
                )}
                {analysisType == "performance" && (
                  <ReportConversionRateChart
                    viewPath={reportsPath}
                    viewPayload={{
                      report_type: "distinct_count",
                      group_by: group_by,
                      report: {
                        website_id: reportWebsiteId,
                        event_type: "View",
                        range_start: range_start,
                        range_end: range_end,
                        variant_id: null,
                        broadcast_id: null,
                        field: null,
                        start_at_first:
                          timeframeType == "All Time" ? true : false,
                      },
                    }}
                    conversionPath={reportsPath}
                    conversionPayload={{
                      report_type: "distinct_count",
                      group_by: group_by,
                      report: {
                        website_id: reportWebsiteId,
                        event_type: "Add To Cart",
                        range_start: range_start,
                        range_end: range_end,
                        variant_id: null,
                        field: null,
                        broadcast_id: null,
                        start_at_first:
                          timeframeType == "All Time" ? true : false,
                      },
                    }}
                    height="200px"
                  />
                )}
              </div>
            </div>
            <div className="row hard-center vertical-space report-column-wrapper">
              {uniqueViewsReport}
              {conversionsReport}
              {conversionRateReport}
              <div className="col-xs-2 center no-padding-sides">
                <ReportCount
                  id={`website-${website.id}-added-to-cart`}
                  label="Added To Cart"
                  tooltip="Unique viewers who added a product to cart from a ConvertFlow campaign"
                  path={reportsPath}
                  payload={{
                    report_type: "distinct_count",
                    report: {
                      website_id: reportWebsiteId,
                      event_type: "Add To Cart",
                      range_start: range_start,
                      range_end: range_end,
                      variant_id: null,
                      field: null,
                      broadcast_id: null,
                    },
                  }}
                  comparison_payload={
                    comparisonRange
                      ? {
                          report_type: "distinct_count",
                          report: {
                            website_id: reportWebsiteId,
                            event_type: "Add To Cart",
                            range_start: comparisonRange.range_start,
                            range_end: comparisonRange.range_end,
                            variant_id: null,
                            field: null,
                            broadcast_id: null,
                          },
                        }
                      : null
                  }
                  callback={() => {
                    updateConversionRate(
                      `#website-${website.id}-views`,
                      `#website-${website.id}-added-to-cart`,
                      `#website-${website.id}-add-to-cart-rate`,
                      { comparison_expected: !!comparisonRange }
                    );
                  }}
                />
              </div>
              <div className="col-xs-2 center no-padding-sides">
                <div className="row hard-center">
                  <div className="row hard-center">
                    <span className="field-title">Cart Add Rate</span>
                    <Tooltip
                      tooltip={"Unique viewers compared to unique adds to cart"}
                      inline={true}
                    />
                  </div>
                  <div className="row hard-center">
                    <h4
                      id={`website-${website.id}-add-to-cart-rate`}
                      className="report-count bold"
                      style={{ margin: "5px 0px 0px" }}
                    >
                      0.00%
                    </h4>
                    <span
                      className="inline-flex items-center px-2 py-1 ml-2 rounded text-base font-medium conversion-rate-comparison-badge conversion-rate-comparison-loading bg-gray-100 text-gray-800"
                      style={{ minWidth: "80px" }}
                    >
                      <span
                        className="cf-loading-spinner reports-spinner"
                        style={{
                          width: "12px",
                          height: "12px",
                          marginRight: "4px",
                        }}
                      ></span>
                    </span>
                  </div>
                </div>
              </div>
              {goals.checkouts && ordersReport}
              {goals.checkouts && ordersRateReport}
            </div>
          </React.Fragment>
        )}
        {goals.checkouts && metric == "orders" && (
          <React.Fragment>
            <div
              className="row hard-center"
              style={{ borderBottom: "1px solid rgb(233, 235, 241)" }}
            >
              <div
                className="col-xs-12 vertical-space center"
                style={{ marginBottom: "0px" }}
              >
                <span className="light text-xl ellipsis">
                  Orders that converted with a campaign within the configured
                  attribution window before purchasing.
                </span>
              </div>
              <div className="col-xs-12 some-vertical-space">
                {analysisType == "funnel" && (
                  <ReportFunnelChart
                    reports={[
                      {
                        label: "Unique Viewers",
                        path: reportsPath,
                        payload: {
                          report_type: "distinct_count",
                          report: {
                            website_id: reportWebsiteId,
                            event_type: "View",
                            variant_id: null,
                            range_start: range_start,
                            range_end: range_end,
                            step_id: null,
                            field: null,
                            broadcast_id: null,
                          },
                        },
                      },
                      {
                        label: "Unique Conversions",
                        path: reportsPath,
                        payload: {
                          report_type: "distinct_count",
                          report: {
                            website_id: reportWebsiteId,
                            event_type: "Complete",
                            variant_id: null,
                            range_start: range_start,
                            range_end: range_end,
                            field: null,
                            broadcast_id: null,
                          },
                        },
                      },
                      {
                        label: "Orders",
                        path: reportsPath,
                        payload: {
                          report_type: "count",
                          report: {
                            website_id: reportWebsiteId,
                            event_type: "Order",
                            contactable_type: "Cta",
                            contactable_id: null,
                            range_start: range_start,
                            range_end: range_end,
                          },
                        },
                      },
                    ]}
                    height="200px"
                  />
                )}
                {analysisType == "counts" && (
                  <ReportChart
                    path={reportsPath}
                    payload={{
                      report_type: "count",
                      group_by: group_by,
                      report: {
                        website_id: reportWebsiteId,
                        event_type: "Order",
                        contactable_type: "Cta",
                        contactable_id: null,
                        range_start: range_start,
                        range_end: range_end,
                        start_at_first:
                          timeframeType == "All Time" ? true : false,
                      },
                    }}
                    height="200px"
                  />
                )}
                {analysisType == "performance" && (
                  <ReportConversionRateChart
                    viewPath={reportsPath}
                    viewPayload={{
                      report_type: "distinct_count",
                      group_by: group_by,
                      report: {
                        website_id: reportWebsiteId,
                        contactable_type: "Cta",
                        event_type: "View",
                        variant_id: null,
                        step_id: null,
                        element_id: null,
                        range_start: range_start,
                        range_end: range_end,
                        step_id: null,
                        field: null,
                        broadcast_id: null,
                        start_at_first:
                          timeframeType == "All Time" ? true : false,
                      },
                    }}
                    conversionPath={reportsPath}
                    conversionPayload={{
                      report_type: "count",
                      group_by: group_by,
                      report: {
                        website_id: reportWebsiteId,
                        event_type: "Order",
                        contactable_type: "Cta",
                        contactable_id: null,
                        range_start: range_start,
                        range_end: range_end,
                        start_at_first:
                          timeframeType == "All Time" ? true : false,
                      },
                    }}
                    height="200px"
                  />
                )}
              </div>
            </div>
            <div className="row hard-center vertical-space report-column-wrapper">
              {uniqueViewsReport}
              {conversionsReport}
              {conversionRateReport}
              {goals.checkouts && ordersReport}
              {goals.checkouts && ordersRateReport}
              {goals.checkouts && revenueReport}
              {goals.checkouts && aovReport}
            </div>
          </React.Fragment>
        )}
        {goals.checkouts && metric == "revenue" && revenueType == "total" && (
          <React.Fragment>
            <div
              className="row hard-center"
              style={{ borderBottom: "1px solid rgb(233, 235, 241)" }}
            >
              <div
                className="col-xs-12 vertical-space center"
                style={{ marginBottom: "0px" }}
              >
                <span className="light text-xl ellipsis">
                  See how much revenue your store has generated
                </span>
              </div>
              <div className="col-xs-12 some-vertical-space">
                <ColumnChart
                  data={props.shopify_revenue}
                  library={{
                    scales: {
                      y: {
                        ticks: {
                          callback: function (value) {
                            return `${props.currency}${value.toLocaleString()}`;
                          },
                        },
                      },
                    },
                    plugins: {
                      tooltip: {
                        callbacks: {
                          label: function (context) {
                            return `${
                              props.currency
                            }${context.formattedValue.toLocaleString()}`;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
            <div className="row hard-center vertical-space report-column-wrapper">
              <div className="col-xs-4 center no-padding-sides">
                <ReportCount
                  id={`website-${website.id}-revenue`}
                  label="Total Revenue This Month"
                  tooltip="How much revenue your Shopify store generated this month"
                  currency={props.currency}
                  count={
                    props.shopify_revenue &&
                    typeof props.shopify_revenue == "object" &&
                    props.shopify_revenue[props.this_month]
                      ? props.shopify_revenue[props.this_month]
                      : 0
                  }
                />
              </div>
              <div className="col-xs-4 center no-padding-sides">
                <ReportCount
                  id={`website-${website.id}-orders`}
                  label="Total Orders This Month"
                  tooltip="How many orders your Shopify store generated this month"
                  count={
                    props.shopify_orders &&
                    typeof props.shopify_orders == "object" &&
                    props.shopify_orders[props.this_month]
                      ? props.shopify_orders[props.this_month]
                      : 0
                  }
                />
              </div>
              <div className="col-xs-4 center no-padding-sides">
                <ReportCount
                  id={`website-${website.id}-aov`}
                  label="AOV This Month"
                  tooltip="The average order value of your Shopify store this month"
                  currency={props.currency}
                  count={
                    props.shopify_revenue &&
                    typeof props.shopify_revenue == "object" &&
                    props.shopify_revenue[props.this_month] &&
                    props.shopify_orders &&
                    typeof props.shopify_orders == "object" &&
                    props.shopify_orders[props.this_month]
                      ? props.shopify_revenue[props.this_month] /
                        props.shopify_orders[props.this_month]
                      : 0
                  }
                />
              </div>
            </div>
          </React.Fragment>
        )}
        {goals.checkouts &&
          metric == "revenue" &&
          revenueType == "attributed" && (
            <React.Fragment>
              <div
                className="row hard-center"
                style={{ borderBottom: "1px solid rgb(233, 235, 241)" }}
              >
                <div
                  className="col-xs-12 vertical-space center"
                  style={{ marginBottom: "0px" }}
                >
                  <span className="light text-xl ellipsis">
                    Revenue attributed to orders that converted with a campaign
                    within the configured attribution window before checkout.
                  </span>
                </div>
                <div className="col-xs-12 some-vertical-space">
                  <ReportChart
                    path={reportsPath}
                    payload={{
                      report_type: "revenue",
                      group_by: group_by,
                      report: {
                        website_id: checkoutsReportWebsiteId,
                        event_type: "Order",
                        contactable_type: "Cta",
                        contactable_id: null,
                        range_start: range_start,
                        range_end: range_end,
                        start_at_first:
                          timeframeType == "All Time" ? true : false,
                      },
                    }}
                    currency={props.currency}
                    height="200px"
                  />
                </div>
              </div>
              <div className="row hard-center vertical-space report-column-wrapper">
                {uniqueViewsReport}
                {conversionsReport}
                {conversionRateReport}
                {goals.checkouts && ordersReport}
                {goals.checkouts && ordersRateReport}
                {goals.checkouts && revenueReport}
                {goals.checkouts && aovReport}
              </div>
            </React.Fragment>
          )}
        {goals.bookings && metric == "bookings" && (
          <React.Fragment>
            <div
              className="row hard-center"
              style={{ borderBottom: "1px solid rgb(233, 235, 241)" }}
            >
              <div
                className="col-xs-12 vertical-space center"
                style={{ marginBottom: "0px" }}
              >
                <span className="light text-xl ellipsis">
                  Meetings booked through scheduler elements in your campaigns.
                </span>
              </div>
              <div className="col-xs-12 some-vertical-space">
                <ReportChart
                  path={reportsPath}
                  payload={{
                    report_type: "distinct_count",
                    group_by: group_by,
                    report: {
                      website_id: bookingsReportWebsiteId,
                      event_type: "Booking",
                      contactable_type: null,
                      contactable_id: null,
                      range_start: range_start,
                      range_end: range_end,
                      variant_id: null,
                      broadcast_id: null,
                      field: null,
                      start_at_first:
                        timeframeType == "All Time" ? true : false,
                    },
                  }}
                  height="200px"
                />
              </div>
            </div>
            <div className="row hard-center vertical-space report-column-wrapper">
              {uniqueViewsReport}
              {bookingsReport}
              {bookingRateReport}
            </div>
          </React.Fragment>
        )}
        {goals.custom && metric == "custom_goals" && (
          <React.Fragment>
            <div
              className="row hard-center"
              style={{ borderBottom: "1px solid rgb(233, 235, 241)" }}
            >
              <div
                className="col-xs-12 vertical-space center"
                style={{ marginBottom: "0px" }}
              >
                <span className="light text-xl ellipsis">
                  Unique visitors who completed {goals.customGoalLabel}.
                </span>
              </div>
              <div className="col-xs-12 some-vertical-space">
                <ReportChart
                  path={reportsPath}
                  payload={{
                    report_type: "distinct_count",
                    group_by: group_by,
                    report: {
                      website_id: customGoalReportWebsiteId,
                      event_type: "Custom Goal",
                      contactable_type: null,
                      contactable_id: null,
                      range_start: range_start,
                      range_end: range_end,
                      variant_id: null,
                      broadcast_id: null,
                      field: null,
                      start_at_first:
                        timeframeType == "All Time" ? true : false,
                    },
                  }}
                  height="200px"
                />
              </div>
            </div>
            <div className="row hard-center vertical-space report-column-wrapper">
              {uniqueViewsReport}
              {customGoalsReport}
              {customGoalRateReport}
            </div>
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  );
};

export default WebsiteConversionReport;
